import React from 'react';
import PanelHelpBadge from '../../PanelHelpBadge';

function ConfidenceBadge({ level }) {
  const normalized = String(level || 'LOW').toUpperCase();
  const label = normalized === 'HIGH' ? 'YÜKSEK' : normalized === 'MEDIUM' ? 'ORTA' : 'DÜŞÜK';
  const cls = normalized === 'HIGH'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
    : normalized === 'MEDIUM'
      ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
      : 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-300';
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${cls}`}>{label}</span>;
}

function PriorityBadge({ level = 'LOW' }) {
  const normalized = String(level || 'LOW').toUpperCase();
  const cls = normalized === 'HIGH'
    ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-300'
    : normalized === 'MEDIUM'
      ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
      : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300';
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${cls}`}>Öncelik: {normalized}</span>;
}

function MonetizationBadge({ level = 'LOW' }) {
  const normalized = String(level || 'LOW').toUpperCase();
  const text = normalized === 'HIGH_INTENT' ? 'Gelir Potansiyeli: YÜKSEK' : normalized === 'MEDIUM' ? 'Gelir Potansiyeli: ORTA' : 'Gelir Potansiyeli: DÜŞÜK';
  return (
    <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700 dark:border-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300">
      {text}
    </span>
  );
}

export default function SegmentSummaryCard({
  segment,
  onOpenDrawer,
  segmentOptions = null,
  onSelectSegmentKey = null
}) {
  const trend = Number(segment?.trendPct || 0);
  const trendPrefix = trend >= 0 ? '+' : '';
  const hasSelector = Array.isArray(segmentOptions) && typeof onSelectSegmentKey === 'function';
  const recommendedAction = segment?.recommendedAction || null;

  return (
    <article className="min-h-[236px] rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-[#101922]">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {hasSelector ? (
            <select
              value={segment.key}
              onChange={(event) => onSelectSegmentKey(event.target.value)}
              className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-sm font-black text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            >
              {segmentOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <h4 className="text-sm font-black text-slate-900 dark:text-white">{segment.name}</h4>
          )}
          <PanelHelpBadge
            title={`${segment.name} Segmenti`}
            description="Segmentin büyüklüğü, davranış sinyalleri ve dönüşüm kalitesi birlikte izlenir."
            interpretation="Güven düzeyi düşükse metrikler karar için tek başına kullanılmamalıdır."
            metricExample="Trend artıp kabul düşüyorsa segmente özel öneri stratejisi gerekir."
          />
        </div>
        <ConfidenceBadge level={segment.confidence} />
      </div>

      <p className="mt-3 text-2xl font-black text-slate-900 dark:text-white">{Number(segment.users || 0).toLocaleString('tr-TR')}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400">kullanıcı</p>
      <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
        Güven: {String(segment.confidence || 'LOW').toUpperCase()} | Örneklem: {Number(segment.sampleSize || segment.users || 0).toLocaleString('tr-TR')}
      </p>
      <p className={`mt-1 text-[11px] font-semibold ${segment.orneklemYeterliMi ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
        {segment.orneklemYeterliMi ? 'Örneklem yeterli' : 'Örneklem düşük'}
      </p>

      <ul className="mt-3 space-y-1.5 text-sm">
        <li className="flex items-center justify-between">
          <span className="text-slate-500 dark:text-slate-400">Aktiflik</span>
          <span className="font-black text-slate-900 dark:text-white">{Number(segment.activityPct || 0).toLocaleString('tr-TR')}%</span>
        </li>
        <li className="flex items-center justify-between">
          <span className="text-slate-500 dark:text-slate-400">Kabul</span>
          <span className="font-black text-slate-900 dark:text-white">{Number(segment.acceptPct || 0).toLocaleString('tr-TR')}%</span>
        </li>
        <li className="flex items-center justify-between">
          <span className="text-slate-500 dark:text-slate-400">Wow</span>
          <span className="font-black text-slate-900 dark:text-white">{Number(segment.wowPct || 0).toLocaleString('tr-TR')}%</span>
        </li>
      </ul>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <PriorityBadge level={segment.priority} />
        <MonetizationBadge level={segment.monetizationPotential} />
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
          Uygunluk: {String(segment.uygunluk || 'yalniz_icgoru').replaceAll('_', ' ')}
        </span>
      </div>
      {recommendedAction ? (
        <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-2 dark:border-indigo-700 dark:bg-indigo-900/20">
          <p className="text-[11px] font-bold text-indigo-700 dark:text-indigo-300">Önerilen aksiyon</p>
          <p className="mt-0.5 text-xs font-semibold text-indigo-900 dark:text-indigo-100">{recommendedAction.title || '-'}</p>
          {recommendedAction.reason ? (
            <p className="mt-0.5 text-[11px] text-indigo-800 dark:text-indigo-200">{recommendedAction.reason}</p>
          ) : null}
        </div>
      ) : null}

      <div className="mt-3 flex items-center justify-between">
        <span className={`text-xs font-bold ${trend >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
          Eğilim {trendPrefix}{trend.toLocaleString('tr-TR')}%
        </span>
        <button
          type="button"
          onClick={() => onOpenDrawer('segment', segment)}
          className="text-xs font-bold text-primary hover:underline"
        >
          Detaya git →
        </button>
      </div>
    </article>
  );
}
